import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';

const brl = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(v) || 0);

const PALETTE = ['text-sky-400 bg-sky-400/10', 'text-emerald-400 bg-emerald-400/10', 'text-amber-400 bg-amber-400/10', 'text-violet-400 bg-violet-400/10', 'text-pink-400 bg-pink-400/10', 'text-primary bg-primary/10', 'text-orange-400 bg-orange-400/10', 'text-teal-400 bg-teal-400/10', 'text-red-400 bg-red-400/10', 'text-foreground/65 bg-muted/40'];
const colorFor = (val, map) => {
  if (map && map[val]) return map[val];
  let h = 0; for (const ch of String(val)) h = (h * 31 + ch.charCodeAt(0)) % PALETTE.length;
  return PALETTE[h];
};

const input = 'w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors placeholder:text-muted-foreground/50';

const ResourceModule = ({ title, subtitle, endpoint, columns, fields, statusKey, statusOptions, statusColors, createLabel = 'Novo' }) => {
  const [list, setList] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => { try { const r = await api.get(`/${endpoint}`); setList(r.data || []); } catch (e) {} };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const filtered = useMemo(() => list.filter((row) => {
    const q = query.toLowerCase();
    if (!q) return true;
    return Object.values(row).some((v) => String(v).toLowerCase().includes(q));
  }), [list, query]);

  const openNew = () => {
    const init = {}; fields.forEach((f) => { init[f.key] = f.type === 'select' && f.options?.length ? f.options[0] : ''; });
    setForm(init); setOpen(true);
  };

  const save = async () => {
    const missing = fields.filter((f) => f.required && !form[f.key]);
    if (missing.length) { toast({ title: 'Campos obrigatórios', description: missing.map((m) => m.label).join(', ') }); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => { if (f.type === 'number' || f.type === 'currency') payload[f.key] = Number(payload[f.key]) || 0; });
      await api.post(`/${endpoint}`, payload);
      setOpen(false); await load();
      toast({ title: `${createLabel} adicionado` });
    } catch (e) { toast({ title: 'Erro ao salvar' }); } finally { setSaving(false); }
  };

  const changeStatus = async (row, value) => {
    setList((prev) => prev.map((r) => r.id === row.id ? { ...r, [statusKey]: value } : r));
    try { await api.put(`/${endpoint}/${row.id}`, { ...row, [statusKey]: value }); } catch (e) { load(); }
  };

  const cell = (row, col) => {
    const val = row[col.key];
    if (col.render) return col.render(row);
    if (col.type === 'currency') return brl(val);
    if (col.type === 'badge') return <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${colorFor(val, col.map)}`}>{val || '—'}</span>;
    return val || <span className="text-muted-foreground/60">—</span>;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">{subtitle || `${list.length} registro(s)`}</p>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-muted/40 border border-border rounded-lg px-3 py-2 w-52">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..." className="bg-transparent outline-none text-sm w-full placeholder:text-muted-foreground/60" />
          </div>
          {fields?.length > 0 && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button onClick={openNew} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold text-sm px-4 py-2.5 rounded-lg transition-colors whitespace-nowrap"><Plus className="w-4 h-4" /> {createLabel}</button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border text-foreground max-w-lg">
                <DialogHeader><DialogTitle className="font-display font-black text-xl uppercase">{createLabel}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4 py-2 max-h-[60vh] overflow-y-auto">
                  {fields.map((f) => (
                    <div key={f.key} className={f.span2 ? 'col-span-2' : ''}>
                      <label className="text-xs text-muted-foreground mb-1.5 block">{f.label}{f.required && <span className="text-primary"> *</span>}</label>
                      {f.type === 'select' ? (
                        <select className={input} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>{(f.options || []).map((o) => <option key={o} className="bg-popover">{o}</option>)}</select>
                      ) : f.type === 'textarea' ? (
                        <textarea rows={2} className={input} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                      ) : (
                        <input type={f.type === 'number' || f.type === 'currency' ? 'number' : (f.type === 'date' ? 'date' : 'text')} className={input} value={form[f.key] || ''} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || ''} />
                      )}
                    </div>
                  ))}
                </div>
                <DialogFooter>
                  <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-lg text-sm font-medium border border-border text-foreground/65 hover:text-foreground transition-colors">Cancelar</button>
                  <button onClick={save} disabled={saving} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-primary hover:bg-primary/90 transition-colors disabled:opacity-60">{saving ? 'Salvando...' : 'Salvar'}</button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-border/70 bg-card p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-muted-foreground text-xs tracking-wider border-b border-border/70">
              {columns.map((c) => <th key={c.key} className={`pb-3 font-semibold ${c.hideMobile ? 'hidden md:table-cell' : ''} ${c.right ? 'text-right' : ''}`}>{c.label}</th>)}
              {statusKey && <th className="pb-3 font-semibold text-right">STATUS</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id} className="border-b border-border/70 hover:bg-muted/40 transition-colors">
                {columns.map((c) => <td key={c.key} className={`py-3.5 ${c.hideMobile ? 'hidden md:table-cell' : ''} ${c.right ? 'text-right' : ''} ${c.strong ? 'font-medium' : 'text-foreground/75'}`}>{cell(row, c)}</td>)}
                {statusKey && (
                  <td className="py-3.5 text-right">
                    <select value={row[statusKey]} onChange={(e) => changeStatus(row, e.target.value)} className={`text-[11px] font-semibold px-2 py-1 rounded-full outline-none cursor-pointer border-0 ${colorFor(row[statusKey], statusColors)}`}>
                      {(statusOptions || []).map((s) => <option key={s} value={s} className="bg-popover text-foreground">{s}</option>)}
                    </select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="text-center py-14 text-muted-foreground">Nenhum registro encontrado.</div>}
      </div>
    </div>
  );
};

export default ResourceModule;
